import { contentTemplateInstanceIsWrapper } from "building-generation/contentTemplates/contentTemplateInstanceWrapper";
import type {
  ContentTemplateInstanceNode,
  ContentTemplateInstanceNodeChildren,
  EvaluatePlanSetTemplateInstanceWithoutBuildingOptions,
  EntityFilterFn,
} from "building-generation/contentTemplates/planSetsEvaluate";
import {
  evaluateContentTemplateInstance,
  evaluatePlanSetTemplateInstanceWithoutBuilding,
} from "building-generation/contentTemplates/planSetsEvaluate";
import { sheetParameterValuesFromSheet } from "building-generation/contentTemplates/sheetParameterValuesFromSheet";
import type { Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationType } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type { DataTable } from "building-types/dataTables/dataTable";
import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Parameter, ParameterRef } from "building-types/options/parameter";
import type { ParameterValue } from "building-types/options/parameter";
import { PlanType } from "building-types/views/BuiltinPlanType";
import type { ViewportCameraKey } from "building-types/views/CameraKey";
import {
  DEFAULT_VIEWPORT_SCALE,
  type DrawingTemplateInstance,
  type EvaluatedDataTable,
  type EvaluatedViewport,
  type SheetTemplateInstance,
  type ViewTemplate,
  type Viewport,
} from "building-types/views/planSets";
import { SheetGeometryType, type SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import degreeToRadian from "common/utils/degreeToRadian";
import { numMul, numSub, sheetPointsNew } from "common/utils/nominalNumbers";
import { collectDisabledOptions } from "doc/options/optionSetTools";
import { isParameterRef } from "doc/parameterTools";
import { buildingBoundsGetDrawingCamera } from "doc/pdf/buildingBoundsGetDrawingCamera";
import { inches } from "doc/pdf/drawingLayout";
import type { PerspectiveDrawingCamera } from "doc/plans/drawingCameraTypes";
import { sheetGeometryTransform } from "doc/plans/sheetGeometryTools";
import { detailOutlineBoxToPolyLine2 } from "doc/utils/annotation";
import { labelFilterPredicate } from "doc/utils/labelTools";
import { viewportSectionView } from "doc/viewTemplateTools";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import { mat3Mult, mat3Rotate, mat3Scale, mat3Translate, type Mat3 } from "geom/mat3";
import { polyLine2AABB2, polyLine2Copy, polyLine2Move, polyLine2NonUniformScale } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByTwoPoints } from "geom/rect2";
import type { PlanPages } from "lib/house/plans/planPagesTypes";
import type { Group, OrthographicDrawing, Page, PerspectiveDrawing, Plan, SheetNode } from "lib/house/plans/sheetNode";
import { SheetNodeType } from "lib/house/plans/sheetNode";
import { getPageMargin } from "lib/house/plans/sheetNodeTools";
import { constructSheetTitle } from "./constructSheetTitle";
import {
  contentTemplateInstanceNodeAnnotationToSheetGeometry,
  contentTemplateInstanceNodeAnnotationToSheetNodes,
} from "./planSetTemplateTools";

interface PlanSetTemplateToPagesOptions
  extends Omit<EvaluatePlanSetTemplateInstanceWithoutBuildingOptions, "entityFilter"> {
  building?: Building;
  filterDisabledOptionSetOverrides?: boolean;
}

function buildDisabledOptionSetEntityFilter(
  filterDisabledOptionSetOverrides: boolean,
  optionSets?: Table<DocOptionSet>,
): EntityFilterFn | undefined {
  if (!filterDisabledOptionSetOverrides || !optionSets) {
    return undefined;
  }

  const disabledOptions = collectDisabledOptions(optionSets);

  return (entity: Viewport | DataTable | Annotation): boolean => {
    if (entity.entityType === "ANNOTATION") {
      return true;
    }

    const optionSetOverridesContainDisabledOption = (
      optionSetOverrides: Table<ParameterRef | DocEntityId<"OPTION">>,
    ): boolean => {
      return Object.values(optionSetOverrides).some((optionId) =>
        isParameterRef(optionId) ? disabledOptions[optionId.id] : disabledOptions[optionId],
      );
    };

    if (entity.entityType === "VIEWPORT" && entity.viewType !== "DRAWING" && entity.optionSetOverrides) {
      return !optionSetOverridesContainDisabledOption(entity.optionSetOverrides);
    }

    if (entity.entityType === "DATA_TABLE" && entity.optionSetOverrides) {
      return !optionSetOverridesContainDisabledOption(entity.optionSetOverrides);
    }

    return true;
  };
}

/**
 * @param shallow Only generate the pages, but skip the page's children
 */
export async function planSetTemplateToPages({
  doc,
  template,
  config,
  globalParameterValues,
  userDefinedOptionChoices,
  shallow,
  building,
  filterDisabledOptionSetOverrides = false,
}: PlanSetTemplateToPagesOptions): Promise<PlanPages> {
  const pages: PlanPages = {
    pages: [],
    layoutMap: {},
  };

  const entityFilter = buildDisabledOptionSetEntityFilter(
    filterDisabledOptionSetOverrides,
    building?.evaluatedOptionSets ?? building?.doc?.optionSets ?? doc.optionSets,
  );

  const templateNode = evaluatePlanSetTemplateInstanceWithoutBuilding({
    doc,
    template,
    config,
    globalParameterValues,
    userDefinedOptionChoices,
    shallow,
    entityFilter,
  });

  for (const node of Object.values(templateNode.sheets)) {
    const template = node.template;

    if (template.templateType === "SHEET") {
      const page = contentTemplateInstanceNodeToSheetNode(node, doc, undefined, undefined, building);

      if (page?.type === SheetNodeType.PAGE) {
        pages.pages.push(page);
      }
    }
  }

  return pages;
}

/** This is only implemented for extracting viewports. Extend to other child types as needed. */
function planSetTemplatePagesToChildren(pages: Page[]): Map<string, EvaluatedViewport[]> {
  const viewports: Map<string, EvaluatedViewport[]> = new Map();

  let currSheetInstanceId: string | undefined = undefined;

  const collectEntities = (node: SheetNode): void => {
    if ("viewport" in node) {
      if (currSheetInstanceId && node.viewport) {
        const existing = viewports.get(currSheetInstanceId);

        if (existing) {
          existing.push(node.viewport);
        } else {
          viewports.set(currSheetInstanceId, [node.viewport]);
        }
      }
    }

    if ("children" in node) {
      for (const child of node.children) {
        collectEntities(child);
      }
    }
  };

  for (const page of pages) {
    if (page.hash) {
      currSheetInstanceId = page.hash;
      collectEntities(page);
    }
  }

  return viewports;
}

export function planSetTemplatePagesWithDuplicateViewportNumbers(pages: Page[]): {
  [sheetInstanceId: string]: EvaluatedViewport[];
} {
  const viewportBySheetInstanceId = planSetTemplatePagesToChildren(pages);

  if (!viewportBySheetInstanceId.size) {
    return {};
  }

  const duplicateViewportsBySheetInstanceId: { [sheetInstanceId: string]: EvaluatedViewport[] } = {};

  for (const [sheetInstanceId, viewports] of viewportBySheetInstanceId.entries()) {
    const viewportsByNumber: { [number: string]: EvaluatedViewport[] } = {};

    for (const viewport of viewports) {
      if (!viewport.number) {
        continue;
      }

      const existing = viewportsByNumber[viewport.number];

      if (existing) {
        existing.push(viewport);
      } else {
        viewportsByNumber[viewport.number] = [viewport];
      }
    }

    const duplicateViewports: EvaluatedViewport[] = [];
    for (const viewportNumber in viewportsByNumber) {
      if (viewportsByNumber[viewportNumber].length > 1) {
        duplicateViewports.push(...viewportsByNumber[viewportNumber]);
      }
    }

    if (duplicateViewports.length > 0) {
      duplicateViewportsBySheetInstanceId[sheetInstanceId] = duplicateViewports.sort((a, b) =>
        String(a.number ?? "").localeCompare(String(b.number ?? "")),
      );
    }
  }

  return duplicateViewportsBySheetInstanceId;
}

// In practice this is only used for drawing templates in drawing viewports.
export function contentTemplateInstanceNodeToSheetGeometry(
  templateInstanceNode: ContentTemplateInstanceNode,
  doc: Doc,
  building?: Building,
  selectorInstance: ContentTemplateInstanceNode | undefined = undefined,
): SheetGeometry[] {
  const { instance, instanceChildren, templateChildren } = templateInstanceNode;

  const result: SheetGeometry[] = [];

  if (
    !selectorInstance &&
    !(
      contentTemplateInstanceIsWrapper(templateInstanceNode.instance) ||
      templateInstanceNode.template.templateType === "SHEET"
    )
  ) {
    selectorInstance = templateInstanceNode;
  }

  function addNodeChildren(nodeChildren: ContentTemplateInstanceNodeChildren) {
    for (const childInstance of Object.values(nodeChildren.childInstances)) {
      const converted = contentTemplateInstanceNodeToSheetGeometry(childInstance, doc, building, selectorInstance);

      if (converted) {
        result.push(...converted);
      }
    }
  }

  addNodeChildren(instanceChildren);
  addNodeChildren(templateChildren);

  const ownAnnotationGeometry = contentTemplateInstanceNodeAnnotationToSheetGeometry(
    templateInstanceNode,
    doc.assets,
    building,
    selectorInstance,
  );

  // Existing child geometry in "result" has already been transformed with accumulated sheet transforms.
  for (const geom of ownAnnotationGeometry) {
    result.push(
      sheetGeometryTransform(geom, {
        transform: templateInstanceNode.sheetTransform,
        transformScale: [1, 1],
        transformRotation: (instance as DrawingTemplateInstance).rotation ?? 0,
      }),
    );
  }

  return result;
}

interface SheetGeometryFromTagTemplateInstancesProps {
  doc: Doc;
  building: Building;
  tagTemplateInstances: DrawingTemplateInstance[];
  planRotationRads: number;
  preserveOrientation: boolean;
  globalParamValues?: Table<ParameterValue>;
  parentParams?: Table<Parameter>;
}

const TEMPLATE_SPACE_TO_SHEET_SPACE_SCALE_FACTOR = 1 / DEFAULT_VIEWPORT_SCALE;
const TEMPLATE_SPACE_TO_SHEET_SPACE_TRANSFORM = mat3Scale(
  TEMPLATE_SPACE_TO_SHEET_SPACE_SCALE_FACTOR,
  TEMPLATE_SPACE_TO_SHEET_SPACE_SCALE_FACTOR,
);

export function makeSheetGeometryFromTagTemplateInstances({
  doc,
  building,
  tagTemplateInstances,
  planRotationRads,
  preserveOrientation,
  globalParamValues = {},
  parentParams = {},
}: SheetGeometryFromTagTemplateInstancesProps): SheetGeometry[] {
  const collectedSheetGeometry: SheetGeometry[] = [];

  // Apply an inverse rotation. planRotationRads is flipped along the y-axis later in the pipeline,
  // so the positive planRotationRads value is correct here.
  const rotation = preserveOrientation ? mat3Rotate(planRotationRads) : null;

  for (const instance of tagTemplateInstances) {
    const transform = mat3Translate(instance.x, instance.y);
    // For room tags templates, sheet geometry moves with the room as the plan rotates,
    // but it should always remain vertical to preserve readability.
    if (preserveOrientation && rotation) {
      mat3Mult(transform, rotation, transform);
    }
    mat3Mult(transform, TEMPLATE_SPACE_TO_SHEET_SPACE_TRANSFORM, transform);

    const instanceNode = evaluateContentTemplateInstance({
      instance: { ...instance, x: 0, y: 0 },
      doc,
      globalParamValues,
      parentParams,
      transform,
    });

    const sheetGeometryForInstance = contentTemplateInstanceNodeToSheetGeometry(instanceNode, doc, building);

    // Multi-element tag templates share a groupId for overlap reduction.
    // The bounding box is constructed lazily in the relaxer to account for styling changes.
    const tagTemplateRelaxationProps = getTagTemplateRelaxationProps(instance, sheetGeometryForInstance);

    for (const sheetGeometry of sheetGeometryForInstance) {
      const geom: SheetGeometry = { ...sheetGeometry, ...tagTemplateRelaxationProps };

      if (preserveOrientation && geom.type === SheetGeometryType.TEXT) {
        // text is handled differently from other rotations in sheetGeometryTools.ts
        // in the sheetGeometryTransform function. The x, y, and rotation values are
        // destructured from the geometry and transformed separately, rather than
        // applying a matrix transform to all components.
        collectedSheetGeometry.push({ ...geom, rotation: planRotationRads });
      } else {
        // Non-TEXT types encode orientation in their transform matrix. No additional
        // rotation override is needed.
        collectedSheetGeometry.push(geom);
      }
    }
  }

  return collectedSheetGeometry;
}

function getTagTemplateRelaxationProps(
  instance: DrawingTemplateInstance,
  sheetGeometryForInstance: SheetGeometry[],
): Pick<SheetGeometry, "groupId"> {
  if (sheetGeometryForInstance.length <= 1) {
    return {};
  }

  const instanceGroupId = `tag-${instance.id}`;

  return { groupId: instanceGroupId };
}

/**
 * @param templateInstanceNode The instance to render
 * @param doc The host document
 * @param selectorInstance The instance that will be selected by clicking on the entity
 * @param building Optional generated building for late-evaluated content template parameters
 */
export function contentTemplateInstanceNodeToSheetNode(
  templateInstanceNode: ContentTemplateInstanceNode,
  doc: Doc,
  labelFilterEntity: LabelableEntityBase | undefined = undefined,
  selectorInstance: ContentTemplateInstanceNode | undefined = undefined,
  building?: Building,
  renderPreviewOutlines: boolean = false,
): Group | Page {
  const { instance, template, instanceChildren, templateChildren } = templateInstanceNode;

  const children: SheetNode[] = [];

  if (
    !selectorInstance &&
    !(
      contentTemplateInstanceIsWrapper(templateInstanceNode.instance) ||
      templateInstanceNode.template.templateType === "SHEET"
    )
  ) {
    selectorInstance = templateInstanceNode;
  }

  function addNodeChildren(nodeChildren: ContentTemplateInstanceNodeChildren) {
    for (const viewport of Object.values(nodeChildren.viewports)) {
      children.push(
        ...viewportToSheetNodes(
          viewport,
          doc,
          selectorInstance,
          templateInstanceNode.sheetTransform,
          renderPreviewOutlines,
        ),
      );
    }

    if (renderPreviewOutlines) {
      for (const annotation of Object.values(nodeChildren.annotation)) {
        if (labelFilterEntity && !labelFilterPredicate(labelFilterEntity, annotation)) {
          continue;
        }
        if (
          annotation.type !== AnnotationType.DETAIL ||
          (annotation.detailType !== "INLINE_IMAGE" && annotation.detailType !== "QR_CODE")
        ) {
          continue;
        }

        const sheetNodeGeometryProps = pathToSheetNodeGeomProps(detailOutlineBoxToPolyLine2(annotation, doc.assets));
        children.push(previewOutlineToSheetNode(sheetNodeGeometryProps, annotation.handle));
      }
    }

    for (const dataTable of Object.values(nodeChildren.dataTables)) {
      children.push(...dataTableToSheetNodes(dataTable, selectorInstance, renderPreviewOutlines));
    }

    for (const childInstance of Object.values(nodeChildren.childInstances)) {
      const converted = contentTemplateInstanceNodeToSheetNode(
        childInstance,
        doc,
        labelFilterEntity,
        selectorInstance,
        building,
        renderPreviewOutlines,
      );

      if (converted) {
        children.push(converted);
      }
    }
  }

  addNodeChildren(instanceChildren);
  addNodeChildren(templateChildren);

  children.push(
    ...contentTemplateInstanceNodeAnnotationToSheetNodes(
      templateInstanceNode,
      doc.assets,
      labelFilterEntity,
      building,
      selectorInstance,
    ),
  );

  if (template.templateType === "SHEET") {
    const margin = getPageMargin(template.margin);

    return {
      type: SheetNodeType.PAGE,
      sheetTitle: constructSheetTitle(instance as SheetTemplateInstance),
      hash: instance.id,
      x: inches(0),
      y: template.height as SheetInches, // TODO: Remove y flip here
      margin,
      width: inches(template.width),
      height: inches(template.height),
      children,
      sheetParameterValues: sheetParameterValuesFromSheet(instance as SheetTemplateInstance),
    };
  }

  if (instance.templateType === "DRAWING") {
    return {
      type: SheetNodeType.GROUP,
      // the negation is due to the fact that we are mirroring the geometry on the y axis here
      rotation: -degreeToRadian(instance.rotation),
      x: instance.x as SheetInches,
      y: numMul(instance.y as SheetInches, -1),
      width: ZERO_INCHES,
      height: ZERO_INCHES,
      children,
      handle: { type: "CONTENT_TEMPLATE_INSTANCE", id: instance.id },
    };
  }

  throw new Error("invalid template type");
}

function viewportToSheetNodes(
  viewport: EvaluatedViewport,
  doc: Doc,
  selectorInstance: ContentTemplateInstanceNode | undefined,
  sheetTransform: Mat3,
  renderPreviewOutlines: boolean = false,
) {
  const children: SheetNode[] = [];

  const handle: DocEntityHandle = selectorInstance
    ? { type: "CONTENT_TEMPLATE_INSTANCE", id: selectorInstance.instance.id }
    : { type: "VIEWPORT", id: viewport.id };

  const sheetNodeGeometryProps = pathToSheetNodeGeomProps(viewport.outline);

  // Draw viewport outline.
  // This should be done even before we check whether the viewport has a view template attached.
  // Viewports without a view template can appear in the editor for example if a parameter for the view template is not set.
  if (renderPreviewOutlines) {
    children.push(previewOutlineToSheetNode(sheetNodeGeometryProps, handle));
  }

  const viewTemplate: ViewTemplate = doc.viewTemplates[viewport.viewTemplateId];
  if (!viewTemplate) {
    return children;
  }

  const sectionView = viewportSectionView(viewport, doc);

  const { pathAtSheetOrigin, x, y, width, height } = sheetNodeGeometryProps;

  function addClippedViewportDrawing(drawing: PerspectiveDrawing | OrthographicDrawing | Plan) {
    children.push({
      type: SheetNodeType.GROUP,
      handle,
      clippingPath: pathAtSheetOrigin,
      x,
      y,
      width,
      height,
      children: [drawing],
    });
  }

  addClippedViewportDrawing({
    type: SheetNodeType.PLAN,
    planType: PlanType.VIEWPORT_PLAN,
    viewport,
    viewTemplate,
    sectionView,
    levelId: "", // determined from the viewport
    x: ZERO_INCHES,
    y: ZERO_INCHES,
    width,
    height,
    sheetTransform,
  });

  return children;
}

function dataTableToSheetNodes(
  dataTable: EvaluatedDataTable,
  selectorInstance: ContentTemplateInstanceNode | undefined,
  renderPreviewOutlines: boolean = false,
) {
  const children: SheetNode[] = [];

  const handle: DocEntityHandle = selectorInstance
    ? { type: "CONTENT_TEMPLATE_INSTANCE", id: selectorInstance.instance.id }
    : { type: "DATA_TABLE", id: dataTable.id };

  // Draw data table outline (like viewports do).
  if (renderPreviewOutlines) {
    const outline = rect2ByTwoPoints(
      dataTable.x,
      dataTable.y,
      dataTable.x + dataTable.width,
      dataTable.y + dataTable.height,
    );
    const sheetNodeGeometryProps = pathToSheetNodeGeomProps(outline);
    children.push(previewOutlineToSheetNode(sheetNodeGeometryProps, handle));
  }

  children.push({
    type: SheetNodeType.DEFERRED_TABLE,
    x: dataTable.x,
    y: numSub(numMul(dataTable.y, -1), dataTable.height),
    dataTable,
    handle,
  });

  return children;
}

export function viewTemplateCamera3D(
  building: Building,
  cameraKey: ViewportCameraKey = "frontLeftAerial",
): PerspectiveDrawingCamera {
  const { buildingBounds } = building;
  const fov = 120;
  const near = 10; // Note: near plane needs to be far enough to mitigate z-fighting artifacts on sheet preview
  const far = 10000;
  const aspect = 1; // Will get patched at the call site.
  const camera = buildingBoundsGetDrawingCamera(buildingBounds, undefined, cameraKey, fov, aspect, near, far);
  return camera;
}

interface OutlineSheetNodeGeomProps {
  readonly pathAtSheetOrigin: PolyLine2;
  readonly x: SheetInches;
  readonly y: SheetInches;
  readonly width: SheetInches;
  readonly height: SheetInches;
}

function pathToSheetNodeGeomProps(path: PolyLine2): OutlineSheetNodeGeomProps {
  let newPath = polyLine2Copy(path);
  newPath = polyLine2NonUniformScale(newPath, 1, -1); // TODO: Remove this Y flipping

  const aabb = polyLine2AABB2(newPath);
  const x = aabb.minX as SheetInches;
  const y = aabb.minY as SheetInches;

  const width = aabb2Width(aabb) as SheetInches;
  const height = aabb2Height(aabb) as SheetInches;

  return {
    pathAtSheetOrigin: polyLine2Move(newPath, -aabb.minX, -aabb.minY),
    x,
    y,
    width,
    height,
  };
}

function previewOutlineToSheetNode(props: OutlineSheetNodeGeomProps, handle?: DocEntityHandle): SheetNode {
  const { pathAtSheetOrigin, x, y } = props;
  return {
    type: SheetNodeType.GEOMETRY,
    handle,
    x,
    y,
    geometry: {
      type: SheetGeometryType.POLYLINE,
      paths: [pathAtSheetOrigin],
      linewidth: sheetPointsNew(0.35),
      strokeColor: "#888888",
      handle,
    },
  };
}
